<template>
  <div class="hb-root" ref="root" :style="rootStyle">
    <div class="hb-stage" :style="{ transform: 'translateX(' + offsetX + 'px) scale(' + scale + ')' }">
      <!-- Stands: a static, slightly blurred layer under the scene (the SVG
           redraws every frame while the ball moves). -->
      <div class="hb-stands" :style="{ backgroundImage: crowd ? 'url(' + crowd + ')' : 'none' }"></div>
      <!-- Broadcast view of the court: one flat SVG where every point (floor,
           boards, goals, ball) goes through the same projection (p3). -->
      <svg class="hb-scene" :viewBox="'0 0 ' + STAGE_W + ' ' + STAGE_H" preserveAspectRatio="none">
        <defs>
          <pattern :id="uid + 'net'" patternUnits="userSpaceOnUse" width="1.6" height="1.6">
            <path d="M0,0 L1.6,1.6 M1.6,0 L0,1.6" stroke="rgba(255,255,255,0.7)" stroke-width="0.25" />
          </pattern>
          <linearGradient :id="uid + 'court'" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0" stop-color="#3a7fae" />
            <stop offset="1" stop-color="#4a92c2" />
          </linearGradient>
          <linearGradient :id="uid + 'roof'" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0" stop-color="#050608" stop-opacity="0.95" />
            <stop offset="1" stop-color="#050608" stop-opacity="0" />
          </linearGradient>
          <radialGradient :id="uid + 'ball'" cx="0.35" cy="0.3" r="0.75">
            <stop offset="0" stop-color="#ffffff" />
            <stop offset="0.55" stop-color="#fde68a" />
            <stop offset="1" stop-color="#d97706" />
          </radialGradient>
          <linearGradient v-if="territory" :id="uid + 'terr'" gradientUnits="userSpaceOnUse" :x1="territory.x1" y1="0" :x2="territory.x2" y2="0">
            <stop offset="0" :stop-color="territory.color" stop-opacity="0" />
            <stop offset="0.7" :stop-color="territory.color" stop-opacity="0.22" />
            <stop offset="1" :stop-color="territory.color" stop-opacity="0.5" />
          </linearGradient>
          <clipPath :id="uid + 'court-clip'">
            <polygon :points="court.floor" />
          </clipPath>
        </defs>

        <!-- Stands. -->
        <rect x="0" y="0" :width="STAGE_W" height="70" :fill="'url(#' + uid + 'roof)'" />

        <!-- Ad-boards along the far side and behind both goals. -->
        <ScrollingBoards :boards="boards" :uid="uid" :logo-url="logoUrl" :board-text="boardText" fill="#0b1f33" top-fill="#16324f" />

        <!-- Floor around the court, then the court itself. -->
        <polygon :points="court.surround" fill="#1d4e74" />
        <polygon :points="court.floor" :fill="'url(#' + uid + 'court)'" />
        <g :clip-path="'url(#' + uid + 'court-clip)'">
          <!-- Possession: a wedge in the team's colour pointing at the goal
               it attacks, its tip following the ball. -->
          <polygon v-if="territory" :points="territory.points" :fill="'url(#' + uid + 'terr)'" class="hb-territory" />
          <!-- Goal areas (6 m), darker. -->
          <polygon v-for="(a, i) in markings.areas" :key="'area' + i" :points="a" fill="#2b6792" />
          <image v-if="showSirplayLogo" :href="PITCH_LOGO" :transform="logoDecal" x="-180" y="-50" width="360" height="100" preserveAspectRatio="none" class="arena-logo" />
          <g fill="none" stroke="rgba(255,255,255,0.92)" stroke-linejoin="round" stroke-linecap="round">
            <path v-for="(d, i) in markings.lines" :key="'l' + i" :d="d" stroke-width="0.9" />
            <path v-for="(d, i) in markings.dashed" :key="'dl' + i" :d="d" stroke-width="0.8" stroke-dasharray="3 2.4" />
          </g>
          <!-- Flat effects on the floor (steal, restart). -->
          <path v-for="f in floorFx" :key="'ffx' + f.key" :d="f.d" class="hb-fx-ring" fill="none" :stroke="f.color" stroke-width="1.4" />
        </g>
        <path :d="court.outline" fill="none" stroke="rgba(255,255,255,0.95)" stroke-width="1" />

        <!-- Goals: net behind, ball when it's inside, then the striped frame. -->
        <g v-for="g in goals" :key="'gb' + g.side" :class="{ 'hb-net-shake': netShake === g.side }">
          <polygon :points="g.back" :fill="'url(#' + uid + 'net)'" stroke="rgba(255,255,255,0.8)" stroke-width="0.4" />
          <polygon :points="g.sideFar" :fill="'url(#' + uid + 'net)'" stroke="rgba(255,255,255,0.6)" stroke-width="0.3" />
          <path :d="g.base" stroke="#e5e7eb" stroke-width="0.7" fill="none" />
        </g>
        <g v-if="ballInNet">
          <circle :cx="ballView.x" :cy="ballView.y" :r="ballView.r" :fill="'url(#' + uid + 'ball)'" />
        </g>
        <g v-for="g in goals" :key="'gf' + g.side" :class="{ 'hb-net-shake': netShake === g.side }">
          <polygon :points="g.sideNear" :fill="'url(#' + uid + 'net)'" stroke="rgba(255,255,255,0.6)" stroke-width="0.3" />
          <polygon :points="g.top" :fill="'url(#' + uid + 'net)'" stroke="rgba(255,255,255,0.6)" stroke-width="0.3" />
          <!-- Red and white posts and crossbar, as on every handball goal. -->
          <path :d="g.frame" stroke="#fff" stroke-width="1.8" fill="none" stroke-linejoin="round" />
          <path :d="g.frame" stroke="#dc2626" stroke-width="1.8" fill="none" stroke-linejoin="round" stroke-dasharray="2.6 2.6" />
        </g>

        <!-- Ball: trail while it flies, shadow on the floor, the ball itself. -->
        <polyline v-if="trail.length > 1" :points="trail.join(' ')" fill="none" stroke="#f97316" stroke-opacity="0.85" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
        <g v-if="!ballInNet && ballVisible">
          <ellipse :cx="ballView.sx" :cy="ballView.sy" :rx="ballView.r * 1.1" :ry="ballView.r * 0.45" fill="rgba(8,20,35,0.35)" />
          <circle :cx="ballView.x" :cy="ballView.y" :r="ballView.r" :fill="'url(#' + uid + 'ball)'" stroke="rgba(120,53,15,0.6)" stroke-width="0.3" />
        </g>

        <!-- Bursts: save at the goal mouth, block, ball out, goal. -->
        <circle v-for="f in fx" :key="'fx' + f.key" :cx="f.x" :cy="f.y" :r="f.r" fill="none" :stroke="f.color" stroke-width="1.6" class="hb-fx-burst" />
      </svg>

      <!-- Label over the ball: the incident just played (shot, save, ...) or
           the team with the ball. -->
      <div v-if="tag" :key="tag.key" class="hb-tag" :style="{ left: ballView.x + 'px', top: (ballView.y - ballView.r - 5) + 'px', '--side-color': tag.color }">{{ tag.text }}</div>

      <!-- Clock. -->
      <div class="hb-clock">
        <b v-if="periodText">{{ periodText }}</b>
        <span>{{ time }}</span>
      </div>

      <!-- 2-minute suspensions still running: the team plays one short. -->
      <div v-if="suspensions.length && !finished" class="hb-susp">
        <span v-for="p in suspensions" :key="'sp' + p.id" class="hb-susp-chip" :style="{ borderColor: p.side === '1' ? homeCol : awayCol }">
          <svg viewBox="0 0 24 24" class="hb-susp-icon"><circle cx="12" cy="7" r="4" fill="currentColor" /><path d="M4,21 a8,7 0 0 1 16,0 Z" fill="currentColor" /></svg>
          {{ clockText(p.left) }}
        </span>
      </div>

      <!-- Card (cards, 2 minutes, 7-metre throw, time-out, half time): dims
           the court for a few seconds (half time: for as long as it lasts). -->
      <transition name="hb-card">
        <div v-if="cardOnScreen" :key="cardOnScreen.key" class="hb-card-veil">
          <div class="hb-card" :style="{ '--side-color': cardOnScreen.color }">
            <span v-if="['yellow', 'red', 'blue'].includes(cardOnScreen.kind)" class="hb-card-icon hb-card-icon--card" :class="'is-' + cardOnScreen.kind"></span>
            <svg v-else-if="cardOnScreen.kind === 'twomin' || cardOnScreen.kind === 'timeout'" class="hb-card-icon" viewBox="0 0 40 40">
              <circle cx="20" cy="22" r="13" fill="none" stroke="#fff" stroke-width="3" />
              <rect x="17" y="3" width="6" height="4" rx="1" fill="#fff" />
              <line x1="20" y1="22" x2="20" y2="13" stroke="#7dd3fc" stroke-width="3" stroke-linecap="round" />
              <line x1="20" y1="22" x2="26" y2="25" stroke="#7dd3fc" stroke-width="3" stroke-linecap="round" />
            </svg>
            <svg v-else-if="cardOnScreen.kind === 'seven'" class="hb-card-icon" viewBox="0 0 40 40">
              <path d="M5,30 V9 H35 V30" fill="none" stroke="#fff" stroke-width="3" stroke-dasharray="4 3" stroke-linejoin="round" />
              <text x="20" y="27" text-anchor="middle" class="hb-card-icon-text">7m</text>
            </svg>
            <svg v-else class="hb-card-icon" viewBox="0 0 40 40">
              <path d="M6,17 a9,9 0 1 0 18,0 H34 V11 H14 A9,9 0 0 0 6,17 Z" fill="#fff" />
              <circle cx="15" cy="18" r="3" fill="#0b1f33" />
              <path d="M28,11 V7" stroke="#fff" stroke-width="3" stroke-linecap="round" />
            </svg>
            <div class="hb-card-title">{{ cardOnScreen.title }}</div>
            <div v-if="cardOnScreen.sub" class="hb-card-sub">{{ cardOnScreen.sub }}</div>
            <div v-if="cardOnScreen.team" class="hb-card-team">{{ cardOnScreen.team }}</div>
          </div>
        </div>
      </transition>

      <!-- Goal, two acts on one full-stage panel: 1) "GOAL" with the scoring
           team over its colour and chevrons running towards the goal; 2)
           split screen in the two teams' colours, the scorer's number rolling
           to the new score. -->
      <transition name="hb-goal-panel">
        <div v-if="goalShown" :key="goalShown.key" class="hb-goal-panel" :style="{ '--goal-color': goalShown.scoreSide === '2' ? awayCol : homeCol }">
          <transition name="hb-goal-act" mode="out-in">
            <div v-if="goalAct === 'word'" key="word" class="hb-goal-act hb-goal-act--word">
              <svg class="hb-goal-chevrons" :class="{ 'is-left': goalShown.scoreSide === '2' }" viewBox="0 0 460 236" preserveAspectRatio="none">
                <polyline v-for="x in [40, 150, 260, 370]" :key="'cv' + x" :points="x + ',-10 ' + (x + 118) + ',118 ' + x + ',246'" fill="none" stroke="var(--goal-color)" stroke-width="2.2" />
                <polyline v-for="x in [40, 150, 260, 370]" :key="'cw' + x" :points="x + ',-10 ' + (x + 118) + ',118 ' + x + ',246'" fill="none" stroke="rgba(255,255,255,0.45)" stroke-width="0.8" />
              </svg>
              <div class="hb-goal-words">
                <div class="hb-goal-word">{{ goalShown.title }}</div>
                <div class="hb-goal-team">{{ goalShown.team }}<template v-if="goalSubtitle"> · {{ goalSubtitle }}</template></div>
              </div>
            </div>
            <div v-else key="split" class="hb-goal-act hb-goal-act--split">
              <div class="hb-goal-half" :style="{ background: halfBg(homeCol) }">
                <span class="hb-goal-digit">
                  <transition name="hb-roll" mode="out-in"><span :key="'h' + rollHome" class="hb-goal-digit-value" :class="{ 'is-intro': !goalRolled }" :style="{ color: textOn(homeCol) }">{{ rollHome }}</span></transition>
                </span>
              </div>
              <div class="hb-goal-half hb-goal-half--away" :style="{ background: halfBg(awayCol) }">
                <span class="hb-goal-digit">
                  <transition name="hb-roll" mode="out-in"><span :key="'a' + rollAway" class="hb-goal-digit-value" :class="{ 'is-intro': !goalRolled }" :style="{ color: textOn(awayCol) }">{{ rollAway }}</span></transition>
                </span>
              </div>
            </div>
          </transition>
        </div>
      </transition>

      <!-- Match over: badge that stays up. -->
      <transition name="hb-final">
        <div v-if="finished" class="hb-final">
          <div class="hb-final-badge">
            <svg class="hb-final-icon" viewBox="0 0 40 40">
              <path d="M12,5 H28 V15 a8,8 0 0 1 -16,0 Z" fill="#facc15" />
              <path d="M12,8 H6 a6,6 0 0 0 6,9 M28,8 H34 a6,6 0 0 1 -6,9" fill="none" stroke="#facc15" stroke-width="2.4" />
              <rect x="18" y="22" width="4" height="7" fill="#eab308" />
              <rect x="12" y="29" width="16" height="5" rx="1.5" fill="#eab308" />
            </svg>
            <div class="hb-final-title">{{ $t('statistics.matchfinished') }}</div>
            <div class="hb-final-score">
              <span :class="{ 'is-win': +scoreH > +scoreA }">{{ homeName }} {{ scoreH }}</span>
              <span class="hb-final-sep">-</span>
              <span :class="{ 'is-win': +scoreA > +scoreH }">{{ scoreA }} {{ awayName }}</span>
            </div>
            <div v-if="periods.length" class="hb-final-periods">
              <span v-for="(p, i) in periods" :key="'p' + i">{{ p.h }}-{{ p.a }}</span>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import ScrollingBoards from '@/components/arenamatchtracker/ScrollingBoards.vue';
import { makeProjection, pt, seg, crowdTexture, makeBoard, stageScaleMixin, floorLogo, PITCH_LOGO, sirplayLicenceMixin } from '@/services/matchtracker/broadcastScene';

// Stage = fixed broadcast frame, scaled to the container width (see stageScaleMixin).
const STAGE_W = 460;
const STAGE_H = 236;
// Court projection (see makeProjection): u spans the court length.
const { proj } = makeProjection({ farY: 100, nearY: 214, farW: 318, nearW: 468, farCx: 230, nearCx: 230 });
// Court in metres (IHF): 40 x 20. X along the length (0 = left goal line),
// Y across (0 = far sideline), H height above the floor.
const CL = 40;
const CW = 20;
const MID_Y = CW / 2;
const AREA_R = 6;
const FREE_THROW_R = 9;
const SEVEN_M = 7;
// Free floor around the court: behind the goals and along the sidelines.
const RUN_END = 2.6;
const RUN_SIDE = 1.6;
// Goals (3 x 2 m), drawn a little bigger so they read at this size.
const GOAL_W = 3.4;
const GOAL_H = 2.3;
const GOAL_D = 1.1;
const BOARD_H = 1.1;
const BALL_R = 0.42;
// Timings (ms), from the ball hitting the net. Goals come every minute or
// so: the panel is short.
const GOAL_PANEL_DELAY = 350;
const GOAL_SPLIT_AT = 2250;
const GOAL_ROLL_AT = 3150;
const GOAL_END_AT = 5250;
const CARD_MS = 4200;
const TAG_MS = 3500;
const FX_MS = 900;
// A goal within this long of a 7-metre throw for the same team is its goal.
const SEVEN_METRE_MS = 45000;
const SUSPENSION_S = 120;
// Incidents shown as a label over the ball (the big ones get a card).
const TAG_TYPES = [16, 96, 2035, 2038, 2046];
const CARD_KINDS = { 6: 'yellow', 7: 'red', 2174: 'blue', 2037: 'twomin', 2036: 'seven', 31: 'timeout' };
// Match-phase pseudo incidents from MatchTracker (second half, kick-off).
const RESTART_TYPES = [9002, 9004];

const rand = (a, b) => a + Math.random() * (b - a);
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
const pick = list => list[Math.floor(Math.random() * list.length)];

// 3D point on the court (metres) -> screen; m = px per metre at that depth.
function p3(X, Y, H = 0) {
  const g = proj(X / CL, Y / CW);
  const m = g.w / CL;
  return { x: g.x, y: g.y - H * m, m };
}
const poly = list => list.map(pt).join(' ');
const pathOf = list => 'M' + list.map(pt).join(' L');
// Home attacks the right goal, away the left one.
const goalX = end => (end === 'R' ? CL : 0);
const attackEnd = team => (team === '1' ? 'R' : 'L');
const attackDir = team => (team === '1' ? 1 : -1);
const CENTRE = { X: CL / 2, Y: MID_Y, H: 0 };
// Point `r` metres out from the goal attacked by `team`, at angle `a`
// (0 = straight in front, +-PI/2 = along the goal line).
const aroundGoal = (team, r, a) => {
  const dir = attackDir(team);
  return { X: goalX(attackEnd(team)) - dir * Math.cos(a) * r, Y: clamp(MID_Y + Math.sin(a) * r, 0.8, CW - 0.8) };
};
// Goal area / free-throw line of the goal at `gx`: a quarter circle around
// each post, joined by a straight line in front of the goal.
function goalArc(gx, r, n = 14) {
  const dir = gx === 0 ? 1 : -1;
  const y0 = MID_Y - 1.5;
  const y1 = MID_Y + 1.5;
  const out = [];
  for (let i = 0; i <= n; i++) {
    const a = -Math.PI / 2 + (Math.PI / 2) * i / n;
    out.push({ X: gx + dir * Math.cos(a) * r, Y: y0 + Math.sin(a) * r });
  }
  for (let i = 0; i <= n; i++) {
    const a = (Math.PI / 2) * i / n;
    out.push({ X: gx + dir * Math.cos(a) * r, Y: y1 + Math.sin(a) * r });
  }
  return out;
}

let fxSeq = 0;

export default {
  name: 'HandballArena',
  components: { ScrollingBoards },
  mixins: [stageScaleMixin, sirplayLicenceMixin],
  props: {
    homeName: { type: String, default: '' },
    awayName: { type: String, default: '' },
    scoreH: { type: [String, Number], default: 0 },
    scoreA: { type: [String, Number], default: 0 },
    // '1' home / '2' away / null.
    possessionSide: { type: String, default: null },
    time: { type: String, default: '' },
    // Match clock in seconds: suspensions run down with it.
    clockSeconds: { type: Number, default: 0 },
    periodText: { type: String, default: '' },
    // Incident on screen (MatchTracker's displayAction): type, side, key, name.
    actionType: { type: Number, default: 0 },
    actionSide: { type: String, default: null },
    actionKey: { type: String, default: '' },
    actionText: { type: String, default: '' },
    // { key, title, subtitle, team, scoreSide, penaltyShot, fromHome, fromAway, homeScore, awayScore }
    goalCelebration: { type: Object, default: null },
    // Half-time break (or any break the feed flags).
    isBreak: { type: Boolean, default: false },
    // Goals per half so far, [{ h, a }] — shown on the final badge.
    periods: { type: Array, default: () => [] },
    finished: { type: Boolean, default: false },
    homeColor: { type: String, default: '' },
    awayColor: { type: String, default: '' },
    logoUrl: { type: String, default: '' },
    boardText: { type: String, default: 'LIVE' }
  },
  data() {
    return {
      PITCH_LOGO,
      STAGE_W,
      STAGE_H,
      ZOOM: 1,
      crowd: '',
      ball: { ...CENTRE },
      ballVisible: true,
      // Scripted play (shot, 7 metres, ...) or null while the ball is passed around.
      seq: null,
      // Team playing the scripted play (the shooter, the stealer, ...).
      seqTeam: null,
      wander: null,
      // Where the possession wedge ends: follows the ball with some lag.
      front: CL / 2,
      trail: [],
      raf: null,
      fx: [],
      floorFx: [],
      fxTimers: [],
      netShake: null,
      cardShown: null,
      cardTimer: null,
      // Card that came in during a goal: shown once the goal is over.
      pendingCard: null,
      tagShown: null,
      tagTimer: null,
      suspensions: [],
      // Team of the last incident: keeps the ball in its half when the feed
      // has no possession reading.
      lastTeam: null,
      // Last 7-metre throw awarded: { team, at }.
      sevenMetre: null,
      // The goal on screen was scored from 7 metres.
      goalSeven: false,
      goalShown: null,
      goalAct: 'word',
      goalRolled: false,
      goalTimers: [],
      rollHome: 0,
      rollAway: 0
    };
  },
  watch: {
    actionKey() {
      if (this.finished) return;
      const t = this.actionType;
      const s = this.actionSide;
      const team = s === '1' || s === '2' ? s : null;
      if (RESTART_TYPES.includes(t)) {
        if (!this.goalShown) this.playToCentre();
        return;
      }
      if (!team) return;
      const other = team === '1' ? '2' : '1';
      // A disqualification also leaves the team one short for 2 minutes.
      if (t === 2037 || t === 7) {
        this.suspensions = [...this.suspensions, { id: this.actionKey, side: team, left: SUSPENSION_S }].slice(-4);
      }
      if (t === 2036) this.sevenMetre = { team, at: Date.now() };
      if (CARD_KINDS[t]) {
        const kind = CARD_KINDS[t];
        const card = {
          key: this.actionKey,
          kind,
          title: this.actionText,
          // A red card also leaves the team one short for 2 minutes.
          sub: kind === 'red' ? `2 ${this.$t('handball.min')}` : '',
          side: team,
          team: this.teamName(team)
        };
        if (this.goalShown) this.pendingCard = card;
        else this.showCard(card);
      }
      if (this.goalShown) return;
      if (TAG_TYPES.includes(t)) this.showTag(this.actionText, team);
      // Save: the side is the goalkeeper's team, the shot comes from the other.
      if (t === 96) { this.lastTeam = other; this.playShot(other, 'save'); }
      else if (t === 2046) { this.lastTeam = team; this.playShot(team, 'wide'); }
      else if (t === 16) { this.lastTeam = team; this.playShot(team, 'blocked'); }
      else if (t === 2036) { this.lastTeam = team; this.playSevenMetre(team); }
      else if (t === 2035) { this.lastTeam = team; this.playSteal(team); }
      else if (t === 2038) { this.lastTeam = other; this.playTurnover(team); }
      else if (CARD_KINDS[t]) this.playStop();
    },
    clockSeconds(n, o) {
      const d = Math.abs(n - o);
      // A jump (new half, clock resync) is not time played.
      if (!d || d > 90 || !this.suspensions.length) return;
      this.suspensions = this.suspensions.map(p => ({ ...p, left: p.left - d })).filter(p => p.left > 0);
    },
    'goalCelebration.key'(key) {
      const g = this.goalCelebration;
      // A goal is only ever replaced by the next goal, never cut short.
      if (!key || !g || this.finished) return;
      this.startGoal(g);
    },
    'goalCelebration.homeScore'(v) { if (this.goalShown && this.goalRolled) this.rollHome = v; },
    'goalCelebration.awayScore'(v) { if (this.goalShown && this.goalRolled) this.rollAway = v; },
    finished(val) {
      if (!val) return;
      this.seq = null;
      this.ball = { ...CENTRE };
      this.suspensions = [];
      this.cardShown = null;
      this.pendingCard = null;
    }
  },
  mounted() {
    this.crowd = crowdTexture('arena');
    this.raf = requestAnimationFrame(this.tick);
  },
  beforeDestroy() {
    cancelAnimationFrame(this.raf);
    clearTimeout(this.cardTimer);
    clearTimeout(this.tagTimer);
    this.goalTimers.forEach(clearTimeout);
    this.fxTimers.forEach(clearTimeout);
  },
  methods: {
    tint(hex, alpha) {
      const m = /^#([0-9a-f]{6})$/i.exec(hex || '');
      if (!m) return `rgba(45,95,174,${alpha})`;
      const n = parseInt(m[1], 16);
      return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${alpha})`;
    },
    isLight(hex) {
      const m = /^#([0-9a-f]{6})$/i.exec(hex || '');
      if (!m) return false;
      const n = parseInt(m[1], 16);
      return (0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) > 185;
    },
    textOn(hex) { return this.isLight(hex) ? '#1f2937' : '#fff'; },
    // Team colour, lighter toward the centre line and darker toward the edge.
    halfBg(hex) {
      return `linear-gradient(90deg, rgba(255,255,255,0.12) 0%, rgba(0,0,0,0.38) 100%), ${hex}`;
    },
    teamName(team) { return team === '1' ? this.homeName : this.awayName; },
    teamCol(team) { return team === '1' ? this.homeCol : (team === '2' ? this.awayCol : '#38bdf8'); },
    showCard(card) {
      clearTimeout(this.cardTimer);
      this.cardShown = { ...card, color: this.teamCol(card.side) };
      this.cardTimer = setTimeout(() => { this.cardShown = null; }, CARD_MS);
    },
    showTag(text, team) {
      if (!text) return;
      clearTimeout(this.tagTimer);
      this.tagShown = { key: 'tag' + (++fxSeq), text, color: this.teamCol(team) };
      this.tagTimer = setTimeout(() => { this.tagShown = null; }, TAG_MS);
    },
    clockText(sec) {
      const s = Math.max(0, Math.ceil(sec));
      return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
    },
    later(fn, ms, list = this.fxTimers) {
      const id = setTimeout(() => {
        const i = list.indexOf(id);
        if (i >= 0) list.splice(i, 1);
        fn();
      }, ms);
      list.push(id);
    },
    burst(X, Y, H, color, r = 7) {
      const p = p3(X, Y, H);
      const key = ++fxSeq;
      this.fx = [...this.fx, { key, x: p.x, y: p.y, r, color }];
      this.later(() => { this.fx = this.fx.filter(f => f.key !== key); }, FX_MS);
    },
    ring(X, Y, color = '#e0f2fe') {
      const key = ++fxSeq;
      const pts = [];
      for (let i = 0; i <= 32; i++) {
        const a = Math.PI * 2 * i / 32;
        pts.push(p3(X + Math.cos(a) * 1.6, Y + Math.sin(a) * 1.6));
      }
      this.floorFx = [...this.floorFx, { key, d: pathOf(pts), color }];
      this.later(() => { this.floorFx = this.floorFx.filter(f => f.key !== key); }, FX_MS);
    },
    onEvent(ev) {
      if (!ev) return;
      const { kind, X, Y, H = 0, side } = ev;
      if (kind === 'save') this.burst(X, Y, H, '#ffffff', 8);
      else if (kind === 'out') this.burst(X, Y, H, '#e2e8f0', 6);
      else if (kind === 'block') this.burst(X, Y, H, '#fbbf24', 6);
      else if (kind === 'ring') this.ring(X, Y, ev.color);
      else if (kind === 'goal') {
        this.burst(X, Y, H, '#4ade80', 11);
        this.netShake = side;
        this.later(() => { this.netShake = null; }, 1400);
      }
    },
    // One animation loop for everything that moves.
    tick(ts) {
      if (this.seq) this.stepSeq(ts);
      else if (!this.finished && !this.isBreak) this.stepWander(ts);
      else if (this.trail.length) this.trail = [];
      this.front += (this.ball.X - this.front) * 0.04;
      this.raf = requestAnimationFrame(this.tick);
    },
    // Idle: the team in possession passes the ball around the free-throw
    // line (and bounces it on the move), probing for a gap.
    stepWander(ts) {
      let w = this.wander;
      if (!w || w.team !== this.playTeam || ts - w.t0 >= w.dur) {
        w = this.nextWander(ts);
        this.wander = w;
      }
      const t = clamp((ts - w.t0) / w.dur, 0, 1);
      const e = w.pass ? 1 - (1 - t) * (1 - t) : t * t * (3 - 2 * t);
      const X = (1 - e) * (1 - e) * w.from.X + 2 * (1 - e) * e * w.ctrl.X + e * e * w.to.X;
      const Y = (1 - e) * (1 - e) * w.from.Y + 2 * (1 - e) * e * w.ctrl.Y + e * e * w.to.Y;
      // A pass travels at chest height, a carried ball is bounced.
      const H = w.pass ? 1.1 + 0.6 * Math.sin(Math.PI * t) : 0.9 * Math.abs(Math.sin(Math.PI * t * w.bounces));
      this.ball = { X, Y, H };
      this.pushTrail(w.pass && w.fast && t < 0.8);
    },
    nextWander(ts) {
      const team = this.playTeam;
      const from = { X: this.ball.X, Y: this.ball.Y };
      const changed = this.wander && this.wander.team !== team;
      let to;
      if (team) {
        // Positional attack: along the 9-metre line, from wing to wing.
        to = aroundGoal(team, rand(9, 12.5), rand(-1.25, 1.25));
      } else {
        to = { X: CL / 2 + rand(-4, 4), Y: MID_Y + rand(-5, 5) };
      }
      const dist = Math.hypot(to.X - from.X, to.Y - from.Y);
      // A new team in possession breaks out with a long pass first.
      const pass = changed || Math.random() < 0.6;
      const bend = pass ? 0 : rand(-0.3, 0.3) * dist;
      const nx = dist ? -(to.Y - from.Y) / dist : 0;
      const ny = dist ? (to.X - from.X) / dist : 0;
      const ctrl = { X: (from.X + to.X) / 2 + nx * bend, Y: clamp((from.Y + to.Y) / 2 + ny * bend, 0.8, CW - 0.8) };
      const dur = pass ? clamp(dist * 60, 420, 1100) : clamp(dist * 260, 900, 2400);
      return { team, from, to, ctrl, pass, fast: changed || dist > 8, bounces: Math.max(2, Math.round(dur / 380)), t0: ts, dur };
    },
    pushTrail(active) {
      if (active) {
        const p = this.ballView;
        this.trail = [...this.trail.slice(-9), pt({ x: p.x, y: p.y })];
      } else if (this.trail.length) {
        this.trail = this.trail.slice(1);
      }
    },
    // Scripted play: a list of { to, dur, ease, peak, fast, ev } segments
    // starting from wherever the ball is now.
    runSeq(segs, team = null) {
      this.seqTeam = team;
      let from = { ...this.ball };
      const list = segs.map(s => {
        const to = { H: 0, ...(s.to || from) };
        const out = { ...s, from, to };
        from = to;
        return out;
      });
      this.seq = { segs: list, i: 0, t0: null };
    },
    stepSeq(ts) {
      const sq = this.seq;
      if (sq.t0 === null) sq.t0 = ts;
      let sg = sq.segs[sq.i];
      let t = (ts - sq.t0) / sg.dur;
      while (t >= 1) {
        this.onEvent(sg.ev);
        sq.i++;
        if (sq.i >= sq.segs.length) {
          this.ball = { ...sg.to };
          this.seq = null;
          this.seqTeam = null;
          this.wander = null;
          return;
        }
        sq.t0 += sg.dur;
        sg = sq.segs[sq.i];
        t = (ts - sq.t0) / sg.dur;
      }
      let e = t;
      if (sg.ease === 'smooth') e = t * t * (3 - 2 * t);
      else if (sg.ease === 'out') e = 1 - (1 - t) * (1 - t);
      const X = sg.from.X + (sg.to.X - sg.from.X) * e;
      const Y = sg.from.Y + (sg.to.Y - sg.from.Y) * e;
      const H = sg.from.H + (sg.to.H - sg.from.H) * e + 4 * (sg.peak || 0) * t * (1 - t);
      this.ball = { X, Y, H: Math.max(0, H) };
      this.pushTrail(!!sg.fast);
    },
    // Shot by `team` at the goal it attacks: 'goal' | 'save' | 'wide' | 'blocked'.
    // `seven`: 7-metre throw, from the 7-metre line. Returns how long until
    // the ball reaches the goal.
    playShot(team, outcome, seven = false) {
      const gx = goalX(attackEnd(team));
      const dir = attackDir(team);
      // Back-court shot, or a wing / pivot one closer in.
      const spot = seven
        ? { X: gx - dir * SEVEN_M, Y: MID_Y, H: 1.9 }
        : { ...aroundGoal(team, pick([rand(9, 11), rand(6.4, 8)]), rand(-1.1, 1.1)), H: 1.9 };
      const approach = clamp(Math.hypot(spot.X - this.ball.X, spot.Y - this.ball.Y) * 90, 250, 900);
      const segs = [{ to: spot, dur: approach, ease: 'smooth' }];
      if (outcome === 'goal') {
        const inNet = { X: gx + dir * (GOAL_D * 0.55), Y: MID_Y + rand(-1.2, 1.2), H: rand(0.3, 1.8) };
        segs.push({ to: inNet, dur: 300, fast: true, peak: 0.3, ev: { kind: 'goal', X: gx, Y: MID_Y, H: GOAL_H / 2, side: attackEnd(team) } });
        segs.push({ to: { X: inNet.X + dir * 0.3, Y: inNet.Y, H: 0 }, dur: 260, ease: 'out' });
        segs.push({ dur: 2600 });
      } else if (outcome === 'save') {
        const mouth = { X: gx - dir * 0.6, Y: MID_Y + rand(-1.2, 1.2), H: rand(0.4, 1.9) };
        segs.push({ to: mouth, dur: 280, fast: true, ev: { kind: 'save', ...mouth } });
        segs.push({ to: { X: gx - dir * rand(2, 4.5), Y: MID_Y + rand(-3, 3), H: 0 }, dur: 600, ease: 'out', fast: true });
        segs.push({ dur: 900 });
      } else if (outcome === 'wide') {
        const out = { X: gx + dir * 0.8, Y: MID_Y + pick([-1, 1]) * rand(GOAL_W / 2 + 0.3, GOAL_W / 2 + 2), H: rand(0.6, 2.6) };
        segs.push({ to: out, dur: 340, fast: true, peak: 0.3, ev: { kind: 'out', ...out } });
        segs.push({ to: { X: gx + dir * rand(1.4, 2.2), Y: out.Y + rand(-1.5, 1.5), H: 0 }, dur: 600, ease: 'out' });
        segs.push({ dur: 700 });
      } else {
        const block = { X: spot.X + dir * rand(1, 1.8), Y: spot.Y + rand(-0.6, 0.6), H: 2.2 };
        segs.push({ to: block, dur: 140, fast: true, ev: { kind: 'block', ...block } });
        segs.push({ to: { X: block.X - dir * rand(2, 4), Y: clamp(block.Y + pick([-1, 1]) * rand(2, 4), 1, CW - 1), H: 0 }, dur: 560, ease: 'out' });
        segs.push({ dur: 700 });
      }
      this.runSeq(segs, team);
      return approach + segs[1].dur;
    },
    // 7-metre throw awarded: the ball goes to the line and waits for the
    // throw (its outcome comes as a goal or a save).
    playSevenMetre(team) {
      const gx = goalX(attackEnd(team));
      const dir = attackDir(team);
      const spot = { X: gx - dir * SEVEN_M, Y: MID_Y };
      this.runSeq([
        { to: spot, dur: 900, ease: 'smooth' },
        { dur: 900, ev: { kind: 'ring', ...spot, color: this.teamCol(team) } },
        { dur: 2600 }
      ], team);
    },
    // Steal by `team`: a pass of the other team intercepted, then the fast break.
    playSteal(team) {
      const dir = attackDir(team);
      const cut = { X: clamp(this.ball.X - dir * rand(1, 3), 2, CL - 2), Y: clamp(this.ball.Y + rand(-3, 3), 1.5, CW - 1.5), H: 1.1 };
      this.runSeq([
        { to: cut, dur: 380, fast: true, ev: { kind: 'ring', X: cut.X, Y: cut.Y, color: this.teamCol(team) } },
        { to: { X: clamp(CL / 2 + dir * rand(4, 8), 2, CL - 2), Y: clamp(cut.Y + rand(-3, 3), 2, CW - 2), H: 0 }, dur: 1100, ease: 'smooth' },
        { dur: 400 }
      ], team);
    },
    // Technical fault by `team`: the ball is lost where it is.
    playTurnover(team) {
      const b = this.ball;
      this.burst(b.X, b.Y, b.H || 0.4, '#f87171', 6);
      const dir = attackDir(team);
      this.runSeq([
        { to: { X: clamp(b.X - dir * rand(1, 2.5), 1, CL - 1), Y: clamp(b.Y + rand(-2, 2), 1, CW - 1), H: 0 }, dur: 600, ease: 'out' },
        { dur: 900 }
      ]);
    },
    // Play stopped (card, suspension, time-out): the ball drops and waits.
    playStop() {
      this.runSeq([
        { to: { X: this.ball.X, Y: this.ball.Y, H: 0 }, dur: 400, ease: 'out' },
        { dur: 2800 }
      ]);
    },
    // Throw-off from the centre (after a goal, at the start of a half).
    playToCentre() {
      this.runSeq([
        { to: { X: CENTRE.X, Y: CENTRE.Y }, dur: 900, ease: 'smooth' },
        { dur: 700, ev: { kind: 'ring', X: CENTRE.X, Y: CENTRE.Y } },
        { dur: 500 }
      ]);
    },
    startGoal(g) {
      this.goalTimers.forEach(clearTimeout);
      this.goalTimers = [];
      clearTimeout(this.cardTimer);
      this.cardShown = null;
      this.tagShown = null;
      const scorer = g.scoreSide === '2' ? '2' : '1';
      const sm = this.sevenMetre;
      const seven = !!g.penaltyShot || !!(sm && sm.team === scorer && Date.now() - sm.at < SEVEN_METRE_MS);
      this.sevenMetre = null;
      this.goalSeven = seven;
      const inNet = this.playShot(scorer, 'goal', seven);
      const at = ms => inNet + ms;
      this.later(() => {
        this.goalShown = g;
        this.goalAct = 'word';
        this.goalRolled = false;
        this.rollHome = g.fromHome;
        this.rollAway = g.fromAway;
      }, at(GOAL_PANEL_DELAY), this.goalTimers);
      this.later(() => { this.goalAct = 'split'; }, at(GOAL_SPLIT_AT), this.goalTimers);
      this.later(() => {
        const cur = this.goalCelebration && this.goalCelebration.key === g.key ? this.goalCelebration : g;
        this.goalRolled = true;
        this.rollHome = cur.homeScore;
        this.rollAway = cur.awayScore;
      }, at(GOAL_ROLL_AT), this.goalTimers);
      this.later(() => {
        this.goalShown = null;
        // Throw-off from the centre for the team that conceded.
        this.seq = null;
        this.ball = { ...CENTRE };
        this.front = CL / 2;
        this.wander = null;
        this.lastTeam = scorer === '1' ? '2' : '1';
        this.playToCentre();
        if (this.pendingCard) {
          this.showCard(this.pendingCard);
          this.pendingCard = null;
        }
      }, at(GOAL_END_AT), this.goalTimers);
    }
  },
  computed: {
    logoDecal() {
      return floorLogo(p3, CL / 2, CW - 2.4, 0.9);
    },
    // SVG ids are document-global: keep each instance's gradients apart.
    uid() { return 'hb' + this._uid + '-'; },
    homeCol() { return this.homeColor || '#2d5fae'; },
    awayCol() { return this.awayColor || '#c0392b'; },
    // Team moving the ball: the feed's possession, else the last incident's.
    playTeam() {
      const p = this.possessionSide;
      if (p === '1' || p === '2') return p;
      return this.lastTeam;
    },
    // Kind of goal (7 metres, power play, ...): can arrive after the goal.
    goalSubtitle() {
      const g = this.goalShown;
      if (!g) return '';
      const live = this.goalCelebration && this.goalCelebration.key === g.key ? this.goalCelebration : g;
      return live.subtitle || (this.goalSeven ? this.$t('handball.sevenmetregoal') : '');
    },
    // Half time: a card that stays up for the whole break.
    breakCard() {
      if (!this.isBreak || this.finished) return null;
      return { key: 'break', kind: 'break', title: this.$t('handball.break'), sub: `${this.scoreH} - ${this.scoreA}`, team: '', color: '#94a3b8' };
    },
    cardOnScreen() {
      if (this.goalShown || this.finished) return null;
      return this.cardShown || this.breakCard;
    },
    // Label over the ball: the incident just played, else who has the ball.
    tag() {
      if (this.goalShown || this.finished || this.isBreak || this.cardOnScreen || this.ballInNet) return null;
      if (this.tagShown) return this.tagShown;
      const team = this.seqTeam || this.playTeam;
      if (!team) return null;
      return { key: 'team' + team, text: this.teamName(team), color: this.teamCol(team) };
    },
    rootStyle() {
      return { height: (STAGE_H * this.scale) + 'px' };
    },
    court() {
      return {
        surround: poly([p3(-RUN_END, -RUN_SIDE), p3(CL + RUN_END, -RUN_SIDE), p3(CL + RUN_END, CW + 4), p3(-RUN_END, CW + 4)]),
        floor: poly([p3(0, 0), p3(CL, 0), p3(CL, CW), p3(0, CW)]),
        outline: pathOf([p3(0, 0), p3(CL, 0), p3(CL, CW), p3(0, CW)]) + ' Z'
      };
    },
    boards() {
      const lo = 0.1;
      const hi = BOARD_H;
      const board = (key, a, b, count, item) => {
        const pa = p3(a.X, a.Y, lo);
        const pb = p3(b.X, b.Y, lo);
        return makeBoard(key, pa, pb, pa.y - p3(a.X, a.Y, hi).y, pb.y - p3(b.X, b.Y, hi).y, count, item);
      };
      return [
        board('far', { X: -RUN_END, Y: -RUN_SIDE }, { X: CL + RUN_END, Y: -RUN_SIDE }, 7, 30),
        board('left', { X: -RUN_END, Y: CW * 0.8 }, { X: -RUN_END, Y: -RUN_SIDE }, 2, 22),
        board('right', { X: CL + RUN_END, Y: -RUN_SIDE }, { X: CL + RUN_END, Y: CW * 0.8 }, 2, 22)
      ];
    },
    markings() {
      const areas = [];
      const lines = [];
      const dashed = [];
      // Centre line, and the substitution-zone marks on the far sideline.
      lines.push(seg(p3(CL / 2, 0), p3(CL / 2, CW)));
      [CL / 2 - 4.5, CL / 2 + 4.5].forEach(X => lines.push(seg(p3(X, -0.5), p3(X, 0.3))));
      [0, CL].forEach(gx => {
        const dir = gx === 0 ? 1 : -1;
        const area = goalArc(gx, AREA_R);
        areas.push(poly(area.map(p => p3(p.X, p.Y))));
        lines.push(pathOf(area.map(p => p3(p.X, p.Y))));
        dashed.push(pathOf(goalArc(gx, FREE_THROW_R).map(p => p3(p.X, p.Y))));
        // 7-metre line and the goalkeeper's 4-metre mark.
        lines.push(seg(p3(gx + dir * SEVEN_M, MID_Y - 0.5), p3(gx + dir * SEVEN_M, MID_Y + 0.5)));
        lines.push(seg(p3(gx + dir * 4, MID_Y - 0.2), p3(gx + dir * 4, MID_Y + 0.2)));
      });
      return { areas, lines, dashed };
    },
    goals() {
      return ['L', 'R'].map(side => {
        const gx = goalX(side);
        const dir = side === 'L' ? 1 : -1;
        const y0 = MID_Y - GOAL_W / 2;
        const y1 = MID_Y + GOAL_W / 2;
        const xb = gx - dir * GOAL_D;
        const xt = gx - dir * GOAL_D * 0.35;
        return {
          side,
          back: poly([p3(xb, y0), p3(xb, y1), p3(xt, y1, GOAL_H), p3(xt, y0, GOAL_H)]),
          sideFar: poly([p3(gx, y0), p3(xb, y0), p3(xt, y0, GOAL_H), p3(gx, y0, GOAL_H)]),
          sideNear: poly([p3(gx, y1), p3(xb, y1), p3(xt, y1, GOAL_H), p3(gx, y1, GOAL_H)]),
          top: poly([p3(gx, y0, GOAL_H), p3(gx, y1, GOAL_H), p3(xt, y1, GOAL_H), p3(xt, y0, GOAL_H)]),
          base: pathOf([p3(gx, y0), p3(xb, y0), p3(xb, y1), p3(gx, y1)]),
          frame: pathOf([p3(gx, y0), p3(gx, y0, GOAL_H), p3(gx, y1, GOAL_H), p3(gx, y1)])
        };
      });
    },
    // Ball behind a goal line, inside the mouth: drawn between net and frame.
    ballInNet() {
      const b = this.ball;
      if (Math.abs(b.Y - MID_Y) > GOAL_W / 2 || b.H > GOAL_H) return false;
      return b.X < 0 || b.X > CL;
    },
    ballView() {
      const b = this.ball;
      const g = p3(b.X, b.Y, 0);
      const up = p3(b.X, b.Y, b.H + BALL_R);
      return { x: up.x, y: up.y, sx: g.x, sy: g.y, r: BALL_R * g.m };
    },
    territory() {
      const team = this.playTeam;
      if (!team || this.finished || this.goalShown || this.isBreak) return null;
      const x0 = team === '1' ? -1 : CL + 1;
      const dir = attackDir(team);
      const front = clamp(this.front + dir * 2, 1, CL - 1);
      // Wedge: full width behind, narrowing to a point just past the ball.
      return {
        points: poly([p3(x0, -1), p3(front - dir * 4, -1), p3(front, MID_Y), p3(front - dir * 4, CW + 1), p3(x0, CW + 1)]),
        x1: p3(x0, MID_Y).x.toFixed(1),
        x2: p3(front, MID_Y).x.toFixed(1),
        color: this.isLight(team === '1' ? this.homeCol : this.awayCol) ? '#e2e8f0' : (team === '1' ? this.homeCol : this.awayCol)
      };
    }
  }
};
</script>

<style scoped>
.hb-root { position: relative; width: 100%; overflow: hidden; background: #101418; }
.hb-stage { position: absolute; top: 0; left: 0; width: 460px; height: 236px; transform-origin: 0 0; font-family: Roboto, Arial, sans-serif; }
.hb-stands { position: absolute; inset: -2px; background-color: #101418; background-size: 90px 45px; background-repeat: repeat; filter: blur(0.6px) brightness(0.7); }
.hb-scene { position: absolute; inset: 0; width: 100%; height: 100%; display: block; }

.hb-territory { animation: hb-territory 2.6s ease-in-out infinite; }
@keyframes hb-territory { 0%, 100% { opacity: 0.75; } 50% { opacity: 1; } }
.hb-net-shake { animation: hb-shake 0.12s linear 10; }
@keyframes hb-shake { 0%, 100% { transform: translate(0, 0); } 25% { transform: translate(0.5px, -0.3px); } 75% { transform: translate(-0.5px, 0.3px); } }
.hb-fx-burst { transform-box: fill-box; transform-origin: 50% 50%; animation: hb-burst 0.9s ease-out forwards; }
@keyframes hb-burst { from { transform: scale(0.2); opacity: 1; } to { transform: scale(1.8); opacity: 0; } }
.hb-fx-ring { transform-box: fill-box; transform-origin: 50% 50%; animation: hb-ring 0.9s ease-out forwards; }
@keyframes hb-ring { from { transform: scale(0.2); opacity: 1; } to { transform: scale(1.6); opacity: 0; } }

/* Label over the ball. */
.hb-tag {
  position: absolute; z-index: 3; transform: translate(-50%, -100%); pointer-events: none;
  padding: 2px 7px; border-radius: 4px; border-bottom: 2px solid var(--side-color);
  background: rgba(255, 255, 255, 0.94); color: #0f172a; white-space: nowrap;
  font: 700 var(--font-size-md)/1.2 'Roboto', sans-serif;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.35);
  transition: left 0.08s linear, top 0.08s linear;
  animation: hb-tag-in 0.3s cubic-bezier(.2, .8, .3, 1.2) both;
}
@keyframes hb-tag-in { from { opacity: 0; transform: translate(-50%, -80%) scale(0.8); } to { opacity: 1; transform: translate(-50%, -100%) scale(1); } }

/* Clock. */
.hb-clock {
  position: absolute; top: 6px; left: 50%; transform: translateX(-50%); z-index: 2;
  display: flex; align-items: center; gap: 6px; padding: 2px 9px;
  background: rgba(0, 0, 0, 0.62); border-radius: 3px;
  font: 700 var(--font-size-3xl)/1.25 'Roboto Mono', 'Roboto', monospace; color: #e0f2fe;
}
.hb-clock b { color: #fff; font-family: Roboto, Arial, sans-serif; }

/* Suspension clocks. */
.hb-susp { position: absolute; top: 6px; left: 8px; z-index: 2; display: flex; flex-direction: column; gap: 3px; }
.hb-susp-chip {
  display: flex; align-items: center; gap: 4px; padding: 1px 7px 1px 5px;
  background: rgba(0, 0, 0, 0.62); border-left: 2px solid; border-radius: 3px;
  font: 700 var(--font-size-2xl)/1.3 'Roboto Mono', 'Roboto', monospace; color: #cbd5e1;
}
.hb-susp-icon { width: 11px; height: 11px; color: #94a3b8; }

/* Incident card. */
.hb-card-veil { position: absolute; inset: 0; z-index: 4; display: flex; align-items: center; justify-content: center; background: rgba(0, 0, 0, 0.32); }
.hb-card {
  display: flex; flex-direction: column; align-items: center; gap: 2px;
  min-width: 170px; padding: 10px 26px 11px; border-radius: 6px;
  background: rgba(15, 35, 55, 0.94); border-bottom: 3px solid var(--side-color);
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.45);
}
.hb-card-icon { width: 40px; height: 40px; margin-bottom: 2px; animation: hb-icon 0.7s cubic-bezier(.2, 1.5, .4, 1) 0.1s both; }
.hb-card-icon--card { width: 24px; height: 34px; border-radius: 3px; box-shadow: 0 3px 8px rgba(0, 0, 0, 0.4); }
.hb-card-icon--card.is-yellow { background: linear-gradient(135deg, #fde047, #eab308); }
.hb-card-icon--card.is-red { background: linear-gradient(135deg, #f87171, #dc2626); }
.hb-card-icon--card.is-blue { background: linear-gradient(135deg, #60a5fa, #1d4ed8); }
.hb-card-icon-text { font: 900 13px 'Roboto', sans-serif; fill: #7dd3fc; }
@keyframes hb-icon { from { transform: scale(0.3) rotate(-20deg); opacity: 0; } to { transform: scale(1) rotate(0); opacity: 1; } }
.hb-card-title { font: italic 900 var(--font-size-9xl)/1.05 'Roboto', sans-serif; color: #fff; white-space: nowrap; animation: hb-rise 0.4s ease-out 0.2s both; }
.hb-card-sub { font: 700 var(--font-size-4xl)/1.2 'Roboto', sans-serif; color: #7dd3fc; animation: hb-rise 0.4s ease-out 0.3s both; }
.hb-card-team { font: 700 var(--font-size-4xl)/1.2 'Roboto', sans-serif; color: #fff; max-width: 220px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; animation: hb-rise 0.4s ease-out 0.38s both; }
@keyframes hb-rise { from { transform: translateY(8px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
.hb-card-enter-active { transition: opacity 0.25s ease; }
.hb-card-enter-active .hb-card { animation: hb-card-in 0.45s cubic-bezier(.2, 1.3, .4, 1) both; }
.hb-card-leave-active { transition: opacity 0.35s ease; }
.hb-card-enter, .hb-card-leave-to { opacity: 0; }
@keyframes hb-card-in { from { transform: scale(0.7); } to { transform: scale(1); } }

/* Goal celebration. */
.hb-goal-panel { position: absolute; inset: 0; z-index: 6; overflow: hidden; background: #0b0b0d; }
.hb-goal-act { position: absolute; inset: 0; }
.hb-goal-act--word {
  display: flex; align-items: center; justify-content: center;
  background: linear-gradient(90deg, rgba(0, 0, 0, 0.55) 0%, rgba(0, 0, 0, 0.72) 100%), var(--goal-color);
}
/* Chevrons running towards the goal the team scored in. */
.hb-goal-chevrons { position: absolute; inset: 0; width: 100%; height: 100%; animation: hb-chevrons 2.2s linear both; filter: drop-shadow(0 0 3px var(--goal-color)); }
.hb-goal-chevrons.is-left { transform: scaleX(-1); animation-name: hb-chevrons-left; }
@keyframes hb-chevrons { from { transform: translateX(-60px); } to { transform: translateX(40px); } }
@keyframes hb-chevrons-left { from { transform: scaleX(-1) translateX(-60px); } to { transform: scaleX(-1) translateX(40px); } }
.hb-goal-words { position: relative; display: flex; flex-direction: column; align-items: flex-start; }
.hb-goal-word {
  font: italic 900 var(--font-size-19xl)/0.95 'Roboto', sans-serif; color: #f1f5f9; text-transform: uppercase;
  text-shadow: 0 3px 0 rgba(0, 0, 0, 0.25);
  animation: hb-goal-word 0.55s cubic-bezier(.2, 1.4, .4, 1) 0.15s both;
}
@keyframes hb-goal-word { from { opacity: 0; transform: translateX(60px) scale(1.6); } to { opacity: 1; transform: translateX(0) scale(1); } }
.hb-goal-team {
  margin-top: 4px; margin-left: 6px; max-width: 260px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  font: italic 800 var(--font-size-8xl)/1.2 'Roboto', sans-serif; color: rgba(255, 255, 255, 0.85);
  animation: hb-goal-team 0.45s ease-out 0.5s both;
}
@keyframes hb-goal-team { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
.hb-goal-act-enter-active { transition: opacity 0.3s ease; }
.hb-goal-act-leave-active { transition: opacity 0.3s ease, transform 0.3s ease; }
.hb-goal-act-enter { opacity: 0; }
.hb-goal-act-leave-to { opacity: 0; transform: translateY(-18px); }

.hb-goal-act--split { display: flex; }
.hb-goal-half { flex: 1; display: flex; align-items: center; justify-content: center; animation: hb-half-in 0.45s cubic-bezier(.2, .9, .2, 1) both; }
.hb-goal-half--away { animation-delay: 0.08s; }
@keyframes hb-half-in { from { transform: translateY(100%); } to { transform: translateY(0); } }
.hb-goal-digit { position: relative; width: 130px; height: 110px; overflow: hidden; }
.hb-goal-digit-value {
  position: absolute; inset: 0; display: flex; align-items: center; justify-content: center;
  font: italic 900 calc(var(--font-size-20xl) * 1.6)/1 'Roboto', sans-serif; font-variant-numeric: tabular-nums;
  text-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
}
.hb-goal-digit-value.is-intro { animation: hb-roll-up 0.55s cubic-bezier(.3, 1.3, .5, 1) 0.4s both; }
@keyframes hb-roll-up { from { transform: translateY(110%); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
.hb-roll-leave-active { transition: transform 0.22s cubic-bezier(.5, 0, .9, .4); }
.hb-roll-leave-to { transform: translateY(-110%); }
.hb-roll-enter-active { transition: transform 0.38s cubic-bezier(.15, .85, .35, 1); }
.hb-roll-enter { transform: translateY(110%); }
.hb-goal-panel-enter-active { transition: transform 0.5s cubic-bezier(.2, .9, .2, 1); }
.hb-goal-panel-leave-active { transition: transform 0.45s cubic-bezier(.6, 0, .8, .3); }
.hb-goal-panel-enter { transform: translateX(-105%); }
.hb-goal-panel-leave-to { transform: translateX(105%); }

/* Match over. */
.hb-final { position: absolute; inset: 0; z-index: 7; display: flex; align-items: center; justify-content: center; background: rgba(5, 10, 16, 0.62); }
.hb-final-badge {
  display: flex; flex-direction: column; align-items: center; gap: 4px; padding: 14px 28px 16px; max-width: 420px;
  background: linear-gradient(160deg, #1f2937 0%, #0b1220 100%); border: 2px solid #facc15; border-radius: 14px;
  box-shadow: 0 0 0 4px rgba(250, 204, 21, 0.15), 0 12px 30px rgba(0, 0, 0, 0.6);
  animation: hb-final-in 0.7s cubic-bezier(.2, 1.4, .4, 1) both;
}
.hb-final-icon { width: 40px; height: 40px; }
.hb-final-title { color: #facc15; font-size: var(--font-size-10xl); font-weight: 900; text-transform: uppercase; white-space: nowrap; }
.hb-final-score { display: flex; gap: 8px; color: #cbd5e1; font-size: var(--font-size-5xl); font-weight: 700; white-space: nowrap; }
.hb-final-score .is-win { color: #fff; }
.hb-final-sep { opacity: 0.6; }
.hb-final-periods { display: flex; gap: 10px; margin-top: 2px; color: #94a3b8; font-size: var(--font-size-2xl); font-weight: 700; }
@keyframes hb-final-in { from { transform: scale(0.3) rotate(-6deg); opacity: 0; } to { transform: scale(1) rotate(0); opacity: 1; } }
.hb-final-enter-active { transition: opacity 0.3s; }
.hb-final-enter { opacity: 0; }
.arena-logo { opacity: 0.9; pointer-events: none; }
</style>
